import { ReactNode, useRef } from "react";
import { motion, useInView, useReducedMotion, Variants } from "framer-motion";

type RevealVariant = "fade-up" | "fade" | "stagger";

interface RevealProps {
  children: ReactNode;
  variant?: RevealVariant;
  delay?: number;
  /** Stagger gap in seconds when variant is "stagger" */
  stagger?: number;
  className?: string;
  as?: "div" | "section" | "article" | "ul" | "header";
  /** intersection threshold 0–1 */
  amount?: number;
}

const ease = [0.22, 1, 0.36, 1] as const;

const Reveal = ({
  children,
  variant = "fade-up",
  delay = 0,
  stagger = 0.06,
  className,
  as = "div",
  amount = 0.15,
}: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount });
  const reduce = useReducedMotion();

  const MotionTag = motion[as] as typeof motion.div;

  if (variant === "stagger") {
    const container: Variants = {
      hidden: {},
      visible: {
        transition: {
          delayChildren: delay,
          staggerChildren: reduce ? 0 : stagger,
        },
      },
    };
    return (
      <MotionTag
        ref={ref as never}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={container}
        className={className}
      >
        {children}
      </MotionTag>
    );
  }

  const distance = reduce || variant === "fade" ? 0 : 14;

  const item: Variants = {
    hidden: { opacity: 0, y: distance },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.9, ease, delay },
    },
  };

  return (
    <MotionTag
      ref={ref as never}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={item}
      className={className}
    >
      {children}
    </MotionTag>
  );
};

/** Child element used inside <Reveal variant="stagger"> */
export const RevealItem = ({
  children,
  className,
  as = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "li" | "article";
}) => {
  const reduce = useReducedMotion();
  const item: Variants = {
    hidden: { opacity: 0, y: reduce ? 0 : 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease } },
  };
  const MotionTag = motion[as] as typeof motion.div;
  return (
    <MotionTag variants={item} className={className}>
      {children}
    </MotionTag>
  );
};

export default Reveal;
